import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";
import { Loader2, Search, Filter, Home, MapPin, GitCompare } from "lucide-react";
import { PropertyComparison } from "@/components/PropertyComparison";

interface Property {
  id: string;
  name: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  bedrooms: number;
  bathrooms: number;
  rent_amount: number;
  status: string;
  description: string;
  landlord_id: string;
}

const PropertySearch = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [properties, setProperties] = useState<Property[]>([]);
  const [filteredProperties, setFilteredProperties] = useState<Property[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [selectedProperties, setSelectedProperties] = useState<string[]>([]);
  const [showComparison, setShowComparison] = useState(false);
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [cityFilter, setCityFilter] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [minBedrooms, setMinBedrooms] = useState("");
  const [maxRent, setMaxRent] = useState("");

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (isAdmin) {
      fetchProperties();
    }
  }, [isAdmin]);

  useEffect(() => {
    applyFilters();
  }, [properties, searchQuery, cityFilter, stateFilter, statusFilter, minBedrooms, maxRent]);

  const checkAuth = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.user) {
        navigate("/auth");
        return;
      }

      setUser(session.user);

      const { data: roleData } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", session.user.id)
        .eq("role", "admin")
        .maybeSingle();

      if (!roleData) {
        toast.error("Access denied. Admin privileges required.");
        navigate("/dashboard");
        return;
      }

      setIsAdmin(true);
    } catch (error: any) {
      console.error("Error checking auth:", error);
      toast.error("Authentication error");
    } finally {
      setLoading(false);
    }
  };

  const fetchProperties = async () => {
    try {
      const { data, error } = await supabase
        .from("properties")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setProperties(data || []);
    } catch (error: any) {
      console.error("Error fetching properties:", error);
      toast.error("Failed to load properties");
    }
  };

  const applyFilters = () => {
    let filtered = [...properties];

    // Search query filter (name, address, city)
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (p) =>
          p.name.toLowerCase().includes(query) ||
          p.address.toLowerCase().includes(query) ||
          p.city.toLowerCase().includes(query)
      );
    }

    // City filter
    if (cityFilter) {
      filtered = filtered.filter((p) =>
        p.city.toLowerCase().includes(cityFilter.toLowerCase())
      );
    }

    // State filter
    if (stateFilter) {
      filtered = filtered.filter((p) =>
        p.state.toLowerCase().includes(stateFilter.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter((p) => p.status === statusFilter);
    }

    // Bedrooms filter
    if (minBedrooms) {
      filtered = filtered.filter((p) => p.bedrooms >= parseInt(minBedrooms));
    }

    // Max rent filter
    if (maxRent) {
      filtered = filtered.filter((p) => p.rent_amount <= parseFloat(maxRent));
    }

    setFilteredProperties(filtered);
  };

  const clearFilters = () => {
    setSearchQuery("");
    setCityFilter("");
    setStateFilter("");
    setStatusFilter("all");
    setMinBedrooms("");
    setMaxRent("");
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, "default" | "secondary" | "outline"> = {
      available: "default",
      occupied: "secondary",
      maintenance: "outline"
    };
    return variants[status] || "outline";
  };

  const handleSelectProperty = (propertyId: string) => {
    setSelectedProperties(prev =>
      prev.includes(propertyId)
        ? prev.filter(id => id !== propertyId)
        : [...prev, propertyId]
    );
  };

  const handleRemoveFromComparison = (propertyId: string) => {
    setSelectedProperties(prev => prev.filter(id => id !== propertyId));
  };

  const selectedPropertyObjects = properties.filter(p => selectedProperties.includes(p.id));

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navigation />
        <main className="flex-1 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </main>
        <Footer />
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />
      
      <main className="flex-1 container mx-auto px-4 py-8 mt-20">
        <div className="max-w-7xl mx-auto space-y-8">
          <div>
            <h1 className="text-4xl font-bold flex items-center gap-2">
              <Search className="h-8 w-8" />
              Property Search
            </h1>
            <p className="text-muted-foreground mt-2">
              Find and match properties with clients
            </p>
          </div>

          {/* Filters Card */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Filter className="h-5 w-5" />
                  <CardTitle>Filters</CardTitle>
                </div>
                <Button variant="outline" size="sm" onClick={clearFilters}>
                  Clear All
                </Button>
              </div>
              <CardDescription>
                Refine your property search
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-3">
                <div>
                  <Label htmlFor="search">Search</Label>
                  <Input
                    id="search"
                    placeholder="Name, address, or city..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    placeholder="Filter by city"
                    value={cityFilter}
                    onChange={(e) => setCityFilter(e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="state">State</Label>
                  <Input
                    id="state"
                    placeholder="Filter by state"
                    value={stateFilter}
                    onChange={(e) => setStateFilter(e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="status">Status</Label>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger id="status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="available">Available</SelectItem>
                      <SelectItem value="occupied">Occupied</SelectItem>
                      <SelectItem value="maintenance">Maintenance</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="bedrooms">Min Bedrooms</Label>
                  <Input
                    id="bedrooms"
                    type="number"
                    min="0"
                    placeholder="e.g., 2"
                    value={minBedrooms}
                    onChange={(e) => setMinBedrooms(e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="maxRent">Max Rent</Label>
                  <Input
                    id="maxRent"
                    type="number"
                    min="0"
                    placeholder="e.g., 2000"
                    value={maxRent}
                    onChange={(e) => setMaxRent(e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Results */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Results</CardTitle>
                  <CardDescription>
                    {filteredProperties.length} propert{filteredProperties.length !== 1 ? "ies" : "y"} found
                  </CardDescription>
                </div>
                {selectedProperties.length > 0 && (
                  <Button onClick={() => setShowComparison(true)} variant="outline">
                    <GitCompare className="mr-2 h-4 w-4" />
                    Compare ({selectedProperties.length})
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {filteredProperties.length === 0 ? (
                <div className="text-center py-12">
                  <Home className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No properties match your filters</p>
                </div>
              ) : (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {filteredProperties.map((property) => (
                    <Card key={property.id} className="overflow-hidden">
                      <CardContent className="p-0">
                        <div className="p-4 space-y-3">
                          <div className="flex items-center gap-2 mb-2">
                            <Checkbox
                              id={`select-${property.id}`}
                              checked={selectedProperties.includes(property.id)}
                              onCheckedChange={() => handleSelectProperty(property.id)}
                            />
                            <Label htmlFor={`select-${property.id}`} className="text-sm cursor-pointer">
                              Select to compare
                            </Label>
                          </div>
                          <div className="flex items-start justify-between">
                            <h3 className="font-semibold text-lg">{property.name}</h3>
                            <Badge variant={getStatusBadge(property.status)}>
                              {property.status}
                            </Badge>
                          </div>
                          
                          <div className="flex items-start gap-2 text-sm text-muted-foreground">
                            <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
                            <div>
                              <p>{property.address}</p>
                              <p>{property.city}, {property.state} {property.zip}</p>
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div>
                              <span className="text-muted-foreground">Bedrooms:</span>
                              <span className="font-medium ml-1">{property.bedrooms}</span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Bathrooms:</span>
                              <span className="font-medium ml-1">{property.bathrooms}</span>
                            </div>
                          </div>

                          <div className="pt-2 border-t">
                            <p className="text-lg font-bold text-primary">
                              ${property.rent_amount.toLocaleString()}/mo
                            </p>
                          </div>

                          {property.description && (
                            <p className="text-sm text-muted-foreground line-clamp-2">
                              {property.description}
                            </p>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      <PropertyComparison
        properties={selectedPropertyObjects}
        open={showComparison}
        onClose={() => setShowComparison(false)}
        onRemove={handleRemoveFromComparison}
      />

      <Footer />
    </div>
  );
};

export default PropertySearch;
